import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { updateDeliveryDetail } from "@/features/offers/cache";
import { useAddDeliveryOptionToDeliveryMutation } from "@/libs/generated/hooks";
import { OptionList } from "../../DeliveryFormV3/components/OptionList";

type AddOptionModalProps = {
  deliveryId: number | undefined;
  organizationSlug: string;
  alreadyLinkedIds: Set<number>;
  open: boolean;
  onClose: () => void;
};

export function AddOptionModal({
  deliveryId,
  organizationSlug,
  alreadyLinkedIds,
  open,
  onClose,
}: AddOptionModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set(alreadyLinkedIds));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(alreadyLinkedIds));
      setError(null);
    }
  }, [open, alreadyLinkedIds]);

  const linkOption = useAddDeliveryOptionToDeliveryMutation();

  const handleToggle = (option: { id: number }) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(option.id)) {
        next.delete(option.id);
      } else {
        next.add(option.id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    setError(null);
    if (!deliveryId) return setError("Delivery not found");

    try {
      const newIds = [...selectedIds].filter((id) => !alreadyLinkedIds.has(id));
      if (newIds.length === 0) {
        onClose();
        return;
      }

      const result = await linkOption.mutateAsync({
        addInput: { deliveryId, optionIds: newIds },
      });

      updateDeliveryDetail(queryClient, deliveryId, (current) => ({
        ...current,
        findDeliveryById: {
          ...current.findDeliveryById,
          selectedOptions: [
            ...(current.findDeliveryById.selectedOptions ?? []),
            ...result.addDeliveryOptionToDelivery,
          ],
        },
      }));

      toast.success(t("delivery.summary.option-added", "Options linked to delivery"));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to link options");
    }
  };

  const handleClose = () => {
    if (linkOption.isPending) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{t("delivery.summary.add-options", "Add options")}</span>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={linkOption.isPending}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <OptionList
          organizationId={organizationSlug}
          selectedIds={selectedIds}
          onToggle={handleToggle}
          disabledIds={alreadyLinkedIds}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="warning"
          variant="outlined"
          fullWidth
          onClick={handleClose}
          disabled={linkOption.isPending}
        >
          {t("cancel", "Cancel")}
        </Button>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          color="success"
          fullWidth
          disabled={linkOption.isPending || selectedIds.size === 0}
          onClick={handleSubmit}
        >
          {t("common.actions.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
